import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import useUiStore from '@/store/uiStore';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { toast } from '@/components/ui/sonner';
import { Check, Hexagon, Octagon, CreditCard, Loader2 } from 'lucide-react';

const PRO_FEATURES = [
  '50 AI credits/month',
  'Unlimited map creation',
  '3-level deep mind maps',
  'AI explanations & Q&A',
  'Export to PNG & PDF',
];

const MAX_FEATURES = [
  '150 AI credits/month',
  'Unlimited map creation',
  'Unlimited depth mind maps',
  'AI explanations & Q&A',
  'Priority support',
];

export default function UpgradeModal() {
  const { isUpgradeModalOpen, closeUpgradeModal } = useUiStore();
  const { refreshUser } = useAuth();
  const [loading, setLoading] = useState(null); // 'PRO' or 'MAX' or null
  const [selectedPlan, setSelectedPlan] = useState('PRO');

  const handleUpgrade = async (plan) => {
    setLoading(plan);
    try {
      // Create Stripe checkout session
      const response = await api.post('/payments/create-checkout', {
        plan: plan,
        origin_url: window.location.origin
      });
      
      // Redirect to Stripe checkout
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (err) {
      console.error('Checkout error:', err);
      toast.error('Failed to start checkout. Please try again.');
      setLoading(null);
    }
  };

  return (
    <Dialog open={isUpgradeModalOpen} onOpenChange={closeUpgradeModal}>
      <DialogContent className="sm:max-w-2xl border-2 border-foreground shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] p-0 gap-0" data-testid="upgrade-modal">
        <div className="p-6">
          <DialogHeader className="mb-6 text-center">
            <DialogTitle className="font-heading text-2xl font-[500] tracking-tight" data-testid="upgrade-title">
              Choose Your Plan
            </DialogTitle>
            <DialogDescription className="font-body text-sm text-muted-foreground mt-1">
              Unlock the full Atlas experience with a plan that fits your needs.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-4">
            {/* Pro Plan */}
            <div 
              className={`border-2 rounded-lg p-5 cursor-pointer transition-all ${
                selectedPlan === 'PRO' ? 'border-cobalt bg-cobalt/5' : 'border-border hover:border-muted-foreground'
              }`}
              onClick={() => setSelectedPlan('PRO')}
              data-testid="plan-pro"
            >
              <div className="flex items-center gap-2 mb-3">
                <Hexagon className="w-5 h-5 text-cobalt" strokeWidth={1.5} />
                <h3 className="font-heading text-lg font-[500]">Pro</h3>
              </div>
              <div className="flex items-baseline gap-1 mb-4">
                <span className="font-heading text-3xl font-[600]">$15</span>
                <span className="font-body text-sm text-muted-foreground">/month</span>
              </div>
              <ul className="space-y-2">
                {PRO_FEATURES.map(f => (
                  <li key={f} className="flex items-center gap-2 text-sm font-body">
                    <Check className="w-4 h-4 text-cobalt flex-shrink-0" strokeWidth={2} />
                    {f}
                  </li>
                ))}
              </ul>
            </div>

            {/* Max Plan */}
            <div 
              className={`border-2 rounded-lg p-5 cursor-pointer transition-all ${
                selectedPlan === 'MAX' ? 'border-orange bg-orange/5' : 'border-border hover:border-muted-foreground'
              }`}
              onClick={() => setSelectedPlan('MAX')}
              data-testid="plan-max"
            >
              <div className="flex items-center gap-2 mb-3">
                <Octagon className="w-5 h-5 text-orange" strokeWidth={1.5} />
                <h3 className="font-heading text-lg font-[500]">Max</h3>
              </div>
              <div className="flex items-baseline gap-1 mb-4">
                <span className="font-heading text-3xl font-[600]">$29</span>
                <span className="font-body text-sm text-muted-foreground">/month</span>
              </div>
              <ul className="space-y-2">
                {MAX_FEATURES.map(f => (
                  <li key={f} className="flex items-center gap-2 text-sm font-body">
                    <Check className="w-4 h-4 text-orange flex-shrink-0" strokeWidth={2} />
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mt-6">
            <p className="text-[10px] text-muted-foreground text-center mb-3">Secure payment via Stripe</p>
            <Button 
              onClick={() => handleUpgrade(selectedPlan)} 
              disabled={loading !== null} 
              data-testid="upgrade-cta-btn"
              className={`w-full h-11 font-body font-medium btn-press ${
                selectedPlan === 'PRO' 
                  ? 'bg-cobalt text-white hover:bg-cobalt/90' 
                  : 'bg-orange text-white hover:bg-orange/90'
              }`}
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Redirecting...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <CreditCard className="w-4 h-4" />
                  Get {selectedPlan === 'PRO' ? 'Pro' : 'Max'} Access
                </span>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
